import { useEffect, useState } from "react"
import { useAuth } from "../contexts/authContext"
import UserNotPermission from "./UserNotPermission"
import PermissionLoadError from "./PermissionLoadError"
import Loading from "../pages/layouts/Loading"
import { withMinimumLoadingDuration } from "../utils/minimumLoadingDuration"
import { useParams } from "react-router-dom"

const RenderIfUserHasPermission = ({ namePermission, alternativePermissions = [], adminOnly = false, children }) => {
    const { getPermissions } = useAuth()
    const { account_id } = useParams()
    const [attempt, setAttempt] = useState(0)
    const [result, setResult] = useState(null)
    const scopeKey = JSON.stringify([account_id, namePermission, alternativePermissions, adminOnly])
    const requestKey = JSON.stringify([scopeKey, attempt])

    useEffect(() => {
        let active = true
        const verify = async () => {
            // Pages outside an account without an access requirement need no lookup.
            if (!account_id && !namePermission && !adminOnly) {
                setResult({ key: requestKey, scopeKey, permissions: {}, statusAccount: {} })
                return
            }
            try {
                const request = getPermissions(account_id, { snapshot: true })
                const snapshot = await (result?.error && result.scopeKey === scopeKey
                    ? withMinimumLoadingDuration(request)
                    : request)
                if (!snapshot?.permissions) throw new Error("Permissions unavailable")
                if (active) setResult({ key: requestKey, scopeKey, ...snapshot })
            } catch {
                if (active) setResult({ key: requestKey, scopeKey, error: true })
            }
        }
        verify()
        return () => { active = false }
    }, [requestKey])

    if (result?.error && result.scopeKey === scopeKey) {
        return <PermissionLoadError loading={result.key !== requestKey} onRetry={() => setAttempt(value => value + 1)} />
    }
    if (result?.key !== requestKey) {
        return <Loading fullPage={true} />
    }

    const { permissions, statusAccount } = result
    const permissionNames = [namePermission, ...alternativePermissions].filter(Boolean)
    const hasPermission = !namePermission || permissionNames.some(name => permissions[name] >= 1)
    const hasAccountManagementAccess = namePermission !== "manage_accounts" || permissions.is_partner > 0 || permissions.is_admin > 0
    const userHasPermission = hasPermission && hasAccountManagementAccess && (!adminOnly || permissions.is_admin > 0)
    const canBypassLicenseBlock = permissions.accessByAdmin || permissions.accessByPartner || permissions.is_admin > 0
    const isFreePage = !namePermission || ["finance", "reports", "manage_users", "manage_roles", "manage_teams"].includes(namePermission)
        || (namePermission === "manage_accounts" && permissions.is_partner > 0)

    if (!canBypassLicenseBlock && !isFreePage && statusAccount?.hasPermission === false) {
        return <UserNotPermission blocked paymentByPartner={statusAccount.payment_by_partner} permissions={permissions} account_id={account_id} />
    }
    return userHasPermission ? children : <UserNotPermission />
}

export default RenderIfUserHasPermission
